import type { LineItem } from '../types';

// CPI projection per BRIEF §7. The cascade compounds y1 → y2 → y3:
//   y1 = current * (1 + r1/100)
//   y2 = y1      * (1 + r2/100)
//   y3 = y2      * (1 + r3/100)
//
// Each year's rate is the per-line override if set, otherwise the global
// default. `null` on an override means "fall back to default"; an explicit
// `0` (a pass-through) overrides to zero and the line stays flat for that
// year. This null-vs-zero distinction is load-bearing — trustees enter `0`
// deliberately for reimbursement / pass-through items.

export type CpiDefaults = {
  cpiY1: number;
  cpiY2: number;
  cpiY3: number;
};

export type ProjectedAmounts = {
  current: number;
  y1: number;
  y2: number;
  y3: number;
};

export type ProjectedLine = LineItem & ProjectedAmounts;

export const projectLine = (item: LineItem, defaults: CpiDefaults): ProjectedLine => {
  const r1 = item.cpiY1 ?? defaults.cpiY1;
  const r2 = item.cpiY2 ?? defaults.cpiY2;
  const r3 = item.cpiY3 ?? defaults.cpiY3;
  const current = item.currentAmount;
  // Fallback chain per year:
  //   1. user manual override (overrideY*)
  //   2. quota-calculator pushed value (quotaY*) — only present on the
  //      bound row, lets cleared overrides revert to the calculator
  //      number rather than the CPI cascade
  //   3. CPI compound from the previous year
  //
  // Once the user overrides a year, we drop the calculator's quotaY*
  // value for any *later* year and let the cascade pick up from the
  // override instead — so editing Y2 propagates into Y3.
  const y1 = item.overrideY1 ?? item.quotaY1 ?? current * (1 + r1 / 100);
  const quotaY2Live = item.overrideY1 == null ? item.quotaY2 : null;
  const y2 = item.overrideY2 ?? quotaY2Live ?? y1 * (1 + r2 / 100);
  const quotaY3Live = item.overrideY1 == null && item.overrideY2 == null ? item.quotaY3 : null;
  const y3 = item.overrideY3 ?? quotaY3Live ?? y2 * (1 + r3 / 100);
  return { ...item, current, y1, y2, y3 };
};

export const projectAll = (items: LineItem[], defaults: CpiDefaults): ProjectedLine[] =>
  items.map((i) => projectLine(i, defaults));
